import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowLeft, Mail, Clock, Sparkles } from "lucide-react";

export const ThankYouMessage = ({ submissionData, onNewSubmission }) => {
  return (
    <Card className="opacity-0 animate-scale-in border-border/60 shadow-xl bg-card/80 backdrop-blur-sm overflow-hidden" style={{ animationDelay: '0.1s' }}>
      {/* Success Header */}
      <div className="relative bg-gradient-to-br from-success/10 via-success/5 to-transparent pt-12 pb-8 px-6 text-center">
        {/* Decorative circles */}
        <div className="absolute top-4 left-4 w-20 h-20 bg-success/10 rounded-full blur-2xl" />
        <div className="absolute bottom-4 right-4 w-16 h-16 bg-primary/10 rounded-full blur-2xl" />
        
        {/* Success Icon */}
        <div className="relative inline-flex items-center justify-center mb-6">
          <div className="absolute inset-0 bg-success/20 rounded-full blur-xl animate-pulse-soft" />
          <div className="relative p-4 bg-success rounded-full shadow-lg animate-checkmark">
            <CheckCircle2 className="h-10 w-10 text-success-foreground" strokeWidth={2.5} />
          </div>
        </div>

        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3">
          Thank You!
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Your video has been submitted successfully. We'll process it and get back to you soon.
        </p>
      </div>

      <CardContent className="p-6 sm:p-8">
        {/* Submission Summary */}
        <div className="bg-muted/50 rounded-xl p-5 mb-6 border border-border/60">
          <h3 className="font-heading text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Submission Summary
          </h3>
          
          <div className="space-y-3">
            <SummaryItem label="Name" value={submissionData?.name} />
            <SummaryItem label="Email" value={submissionData?.email} />
            <SummaryItem label="Project" value={submissionData?.project_name} />
            {submissionData?.submissionId && (
              <SummaryItem 
                label="Reference ID" 
                value={submissionData.submissionId.slice(0, 8) + '...'} 
                isCode 
              />
            )}
          </div>
        </div>

        {/* What's Next */}
        <div className="space-y-4 mb-8">
          <h3 className="font-heading text-sm font-semibold text-foreground flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            What's Next?
          </h3>
          
          <div className="space-y-3">
            <NextStep 
              number={1} 
              title="Video Processing"
              description="We'll download and process your video automatically"
            />
            <NextStep 
              number={2} 
              title="Quality Check"
              description="Our team will review the processed output"
            />
            <NextStep 
              number={3} 
              title="Delivery"
              description="You'll receive the final files via email"
              icon={<Mail className="h-4 w-4" />}
            />
          </div>
        </div>

        {/* Action Button */}
        <Button 
          variant="outline" 
          onClick={onNewSubmission}
          className="w-full h-11 border-border hover:bg-muted hover:border-primary/30 transition-all duration-300"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Submit Another Video
        </Button>
      </CardContent>
    </Card>
  );
};

const SummaryItem = ({ label, value, isCode }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-muted-foreground">{label}</span>
    {isCode ? (
      <code className="text-sm font-mono text-foreground bg-background px-2 py-0.5 rounded border border-border">
        {value}
      </code>
    ) : (
      <span className="text-sm font-medium text-foreground truncate max-w-[200px]">{value}</span>
    )}
  </div>
);

const NextStep = ({ number, title, description, icon }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border/60 hover:border-primary/20 transition-colors duration-200">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
      {icon || <span className="text-xs font-bold text-primary">{number}</span>}
    </div>
    <div>
      <h4 className="text-sm font-medium text-foreground">{title}</h4>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
  </div>
);
